/**
 * usePocPacingData — pulls POC pacing data for the POC Pacing Chart (P5
 * of the Dashboard build).
 *
 * For the selected client + metric, returns:
 *   - POC window (start_date + length derived from clients.package_signed)
 *   - M1/M2/M3 goal milestones (from client_goals table, when present
 *     for the selected metric)
 *   - Day-by-day actuals (1 .. day_of_poc) for the selected metric
 *   - Diagnostic message
 *
 * Decoupled-state design (Sabeeh, 2026-04-30 — fixes the Sends-tab bug):
 *   POC WINDOW (Phase card, day-of-POC, header date pill) and
 *   PER-METRIC TARGETS (Goal card, Forecast card, dashed goal line on
 *   the chart) are independent. A client that has any POC config
 *   should always see Phase / day-of-POC / actuals line, even on a
 *   metric tab whose `client_goals` row hasn't been seeded yet.
 *
 *   POC window resolution priority:
 *     1. clients.package_signed → length (3 Month POC = 90 days,
 *        Month to Month recurring = no POC pacing)
 *     2. clients.onboarding_date → POC start
 *     3. Fallback: any client_goals row's poc_start_date and length
 *        (so we still work when onboarding_date hasn't been backfilled
 *        from Attio yet)
 *
 *   Per-metric target resolution:
 *     - client_goals row matching (client_id, metric_name) provides
 *       m1/m2/m3. If absent, no goal line is drawn.
 *
 * Data sources (read-only, no schema changes):
 *   - clients                 — POC window from package_signed + onboarding_date
 *   - client_goals            — per-metric M1/M2/M3 targets
 *   - masterinbox_replies     — replies / positives / meetings daily counts,
 *                                 scoped by workspace_name = clients.name
 *   - obsync_dynamic_reports  — sends daily counts (EMAIL_SENT events),
 *                                 scoped by workspace_name = clients.name
 *   - closed / acv / pipeline — actuals not yet wired (deals_won is now a
 *                                 thin Clay landing table; pipeline has no
 *                                 source yet). Returns 0 actuals so the
 *                                 chart still renders the goal line.
 */
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'

export type PacingMetric =
  | 'sends'
  | 'replies'
  | 'positives'
  | 'meetings'
  | 'closed'
  | 'pipeline'
  | 'acv'

export const PACING_METRIC_LABELS: Record<PacingMetric, string> = {
  sends: 'Sends',
  replies: 'Replies',
  positives: '★ Positives',
  meetings: 'Meetings',
  closed: 'Closed',
  pipeline: 'Pipeline',
  acv: 'Won ACV',
}

function metricNameForDb(m: PacingMetric): string {
  switch (m) {
    case 'sends':     return 'sends'
    case 'replies':   return 'replies'
    case 'positives': return 'positives'
    case 'meetings':  return 'meetings'
    case 'closed':    return 'closed'
    case 'pipeline':  return 'pipeline'
    case 'acv':       return 'won_acv'
  }
}

/**
 * Derive POC length in days from the package text. Packages enum is
 * defined in src/types/client.ts (PACKAGE_OPTIONS). Returns null when
 * the package implies no fixed POC window (e.g. "Month to Month
 * recurring") — the card hides itself in that case.
 */
function pocLengthFromPackage(pkg: string | null | undefined): number | null {
  if (!pkg) return null
  const trimmed = pkg.trim()
  if (trimmed === '3 Month POC') return 90
  if (trimmed === 'Month to Month recurring') return null
  return null
}

export interface PocPacingPoint {
  day: number
  actual: number | null
  goal: number | null
}

export interface PocPacingData {
  /** True when the client has a POC window (package_signed parsed OR client_goals row found). */
  hasPocWindow: boolean
  /** True when the selected metric has its own M1/M2/M3 row in client_goals. */
  hasMetricTarget: boolean
  /**
   * @deprecated alias for `hasPocWindow && hasMetricTarget` — older callers
   * may still reference this; kept so the component renders correctly until
   * fully migrated to the two-flag model.
   */
  hasGoals: boolean
  /** Why the card is empty (when hasPocWindow=false). UI shows the right copy. */
  emptyReason: 'no_client_selected' | 'no_package_signed' | 'recurring_no_poc' | 'no_pocstart' | null
  /** Client display name (for the editor modal title). */
  clientName: string | null
  pocStartDate: string | null
  pocEndDate: string | null
  dayOfPoc: number
  totalDays: number
  m1Target: number
  m2Target: number
  m3Target: number
  currentActual: number
  /** Projected end-of-POC value, computed using the client's forecast_model. */
  forecastDay90: number
  /** Which projection method the forecast uses. NULL/missing → 'linear'. */
  forecastModel: 'linear' | 'trailing_7' | 'trailing_14'
  series: PocPacingPoint[]
  diagnostic: string | null
  loading: boolean
}

function goalAtDay(day: number, m1: number, m2: number, m3: number): number {
  if (day <= 30) return (day / 30) * m1
  if (day <= 60) return m1 + ((day - 30) / 30) * (m2 - m1)
  return m2 + ((day - 60) / 30) * (m3 - m2)
}

function diagnose(
  metric: PacingMetric,
  actual: number,
  expected: number,
  dayOfPoc: number,
  hasMetricTarget: boolean,
): string | null {
  if (!hasMetricTarget) {
    return `Tracking ${metric} actuals — set an M1/M2/M3 goal in client_goals to enable pacing diagnostics`
  }
  if (expected === 0) {
    return dayOfPoc < 3
      ? 'POC just started — pace data accumulating'
      : 'No goal benchmark yet for today'
  }
  const ratio = actual / expected
  if (ratio >= 1.1) return `${Math.round((ratio - 1) * 100)}% ahead of pace · strong signal`
  if (ratio >= 0.9) return 'On pace · tracking to goal'
  if (ratio >= 0.7) return `${Math.round((1 - ratio) * 100)}% behind pace · watch closely`
  return `${Math.round((1 - ratio) * 100)}% behind pace · needs attention`
}

const EMPTY: PocPacingData = {
  hasPocWindow: false,
  hasMetricTarget: false,
  hasGoals: false,
  emptyReason: 'no_client_selected',
  clientName: null,
  pocStartDate: null,
  pocEndDate: null,
  dayOfPoc: 0,
  totalDays: 90,
  m1Target: 0,
  m2Target: 0,
  m3Target: 0,
  currentActual: 0,
  forecastDay90: 0,
  forecastModel: 'linear',
  series: [],
  diagnostic: null,
  loading: false,
}

export function usePocPacingData(
  selectedClient: string | 'all' | undefined,
  metric: PacingMetric,
): PocPacingData {
  const clientId = selectedClient === 'all' ? undefined : selectedClient

  const query = useQuery({
    queryKey: ['poc-pacing', clientId ?? 'all', metric],
    enabled: !!clientId,
    queryFn: async (): Promise<PocPacingData> => {
      if (!clientId) return EMPTY

      // 1) Resolve POC window — independent of metric. This is the
      // critical decoupling: the Phase card and date pill should
      // render whenever the client has a POC, even on a metric tab
      // with no targets yet.
      const { data: clientRow } = await (supabase as any)
        .from('clients')
        .select(
          'name, package_signed, onboarding_date, poc_length_days, poc_length_unit, forecast_model'
        )
        .eq('id', clientId)
        .maybeSingle()

      if (!clientRow) {
        return { ...EMPTY, emptyReason: 'no_client_selected' }
      }
      const clientName: string | null = clientRow.name ?? null

      // POC length resolution (highest precedence first):
      //   1. explicit clients.poc_length_unit='relationship' → no POC window
      //   2. explicit clients.poc_length_days (set via the Step 4 editor)
      //   3. derivation from clients.package_signed
      // 'relationship' unit always means open-ended (no fixed POC window),
      // matching Jamie's spec for retainer-style engagements.
      if (clientRow.poc_length_unit === 'relationship') {
        return { ...EMPTY, emptyReason: 'recurring_no_poc', clientName }
      }

      const pkgLength = pocLengthFromPackage(clientRow.package_signed)
      // Recurring packages have no POC pacing — hide cleanly. Skip this
      // check if the client has been explicitly given a poc_length_days
      // override (admin overrode the recurring default).
      if (
        clientRow.package_signed === 'Month to Month recurring' &&
        clientRow.poc_length_days == null
      ) {
        return { ...EMPTY, emptyReason: 'recurring_no_poc', clientName }
      }

      // POC start: prefer onboarding_date (Attio source-of-truth, NULL
      // until Kunal's sync ships). Fall back to the earliest
      // poc_start_date across the client_goals rows.
      let pocStartIso: string | null = clientRow.onboarding_date ?? null
      let pocLengthDays: number | null =
        typeof clientRow.poc_length_days === 'number'
          ? clientRow.poc_length_days
          : pkgLength
      let goalRow: any = null

      const { data: goalsForClient } = await (supabase as any)
        .from('client_goals')
        .select('metric_name, poc_start_date, poc_end_date, m1_target, m2_target, m3_target')
        .eq('client_id', clientId)
        .order('poc_start_date', { ascending: true })

      const goalsList = (goalsForClient ?? []) as any[]

      // Monthly mode: client opted into per-calendar-month pacing. Pick the
      // goals row whose date range contains today (each row covers a single
      // calendar month). Falls through to "no_pocstart" if no matching row,
      // so admins see the empty-state hint to set this month's targets.
      if (clientRow.poc_length_unit === 'monthly') {
        const todayIso = new Date().toISOString().slice(0, 10)
        const currentMonthRow = goalsList.find(
          (g) => g.poc_start_date <= todayIso && g.poc_end_date >= todayIso,
        )
        if (currentMonthRow) {
          pocStartIso = currentMonthRow.poc_start_date
          const start = new Date(currentMonthRow.poc_start_date)
          const end = new Date(currentMonthRow.poc_end_date)
          pocLengthDays = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1
        }
      } else {
        if (!pocStartIso && goalsList.length > 0) {
          pocStartIso = goalsList[0].poc_start_date
        }

        if (!pkgLength && goalsList.length > 0) {
          // Derive length from the first row's start/end if package wasn't parseable.
          const firstStart = new Date(goalsList[0].poc_start_date)
          const firstEnd = new Date(goalsList[0].poc_end_date)
          const days = Math.round((firstEnd.getTime() - firstStart.getTime()) / 86_400_000)
          if (Number.isFinite(days) && days > 0) pocLengthDays = days
        }
      }

      if (!pocStartIso || !pocLengthDays) {
        const reason: PocPacingData['emptyReason'] = !clientRow.package_signed
          ? 'no_package_signed'
          : 'no_pocstart'
        return { ...EMPTY, emptyReason: reason, clientName }
      }

      const start = new Date(pocStartIso)
      const totalDays = pocLengthDays
      const end = new Date(start.getTime() + totalDays * 86_400_000)
      const now = new Date()
      const dayOfPoc = Math.min(
        totalDays,
        Math.max(1, Math.round((now.getTime() - start.getTime()) / 86_400_000) + 1),
      )

      // 2) Per-metric targets. Look up in goalsList (single round-trip).
      goalRow = goalsList.find((g) => g.metric_name === metricNameForDb(metric)) ?? null
      const hasMetricTarget = !!goalRow
      const m1 = hasMetricTarget ? Number(goalRow.m1_target) || 0 : 0
      const m2 = hasMetricTarget ? Number(goalRow.m2_target) || 0 : 0
      const m3 = hasMetricTarget ? Number(goalRow.m3_target) || 0 : 0

      // 3) Daily actuals over the POC window.
      const dailyActuals: Record<number, number> = {}
      const bumpDay = (isoDate: string | Date, amount = 1) => {
        const d = typeof isoDate === 'string' ? new Date(isoDate) : isoDate
        if (Number.isNaN(d.getTime())) return
        const dayNum = Math.round((d.getTime() - start.getTime()) / 86_400_000) + 1
        if (dayNum >= 1 && dayNum <= totalDays) {
          dailyActuals[dayNum] = (dailyActuals[dayNum] ?? 0) + amount
        }
      }

      try {
        if (metric === 'replies' || metric === 'positives' || metric === 'meetings') {
          let q = (supabase as any)
            .from('masterinbox_replies')
            .select('created_at, reply_category_type, label_name, workspace_name')
            .gte('created_at', start.toISOString())
            .lte('created_at', end.toISOString())
          if (clientName) q = q.eq('workspace_name', clientName)
          const { data: replies } = await q
          for (const r of (replies ?? []) as any[]) {
            if (metric === 'positives' && r.reply_category_type !== 'positive') continue
            if (metric === 'meetings' && r.label_name !== 'Meeting Booked') continue
            bumpDay(r.created_at)
          }
        } else if (metric === 'sends') {
          let q = (supabase as any)
            .from('obsync_dynamic_reports')
            .select('created_at, workspace_name, event_type')
            .eq('event_type', 'EMAIL_SENT')
            .gte('created_at', start.toISOString())
            .lte('created_at', end.toISOString())
          if (clientName) q = q.eq('workspace_name', clientName)
          const { data: sends } = await q
          for (const s of (sends ?? []) as any[]) bumpDay(s.created_at)
        }
        // closed / acv / pipeline still pending real sources.
      } catch {
        // Fall through with empty actuals.
      }

      const series: PocPacingPoint[] = []
      let cumulativeActual = 0
      for (let day = 1; day <= totalDays; day++) {
        if (day <= dayOfPoc) {
          cumulativeActual += dailyActuals[day] ?? 0
        }
        series.push({
          day,
          actual: day <= dayOfPoc ? cumulativeActual : null,
          goal: hasMetricTarget ? goalAtDay(day, m1, m2, m3) : null,
        })
      }

      const currentActual = cumulativeActual
      const expectedNow = hasMetricTarget ? goalAtDay(dayOfPoc, m1, m2, m3) : 0
      const diagnostic = diagnose(metric, currentActual, expectedNow, dayOfPoc, hasMetricTarget)

      // ── Forecast model resolution ───────────────────────────────────
      // linear      = (currentActual / dayOfPoc) × totalDays  (default)
      // trailing_7  = currentActual + (avg of last 7d daily actuals) × remainingDays
      // trailing_14 = currentActual + (avg of last 14d daily actuals) × remainingDays
      // Falls back to 'linear' for null / unrecognised values.
      const rawModel = String(clientRow.forecast_model ?? '').trim()
      const forecastModel: PocPacingData['forecastModel'] =
        rawModel === 'trailing_7' || rawModel === 'trailing_14' ? rawModel : 'linear'

      const remainingDays = Math.max(totalDays - dayOfPoc, 0)
      let forecastDay90 = 0
      if (dayOfPoc > 0) {
        if (forecastModel === 'linear') {
          forecastDay90 = Math.round((currentActual / dayOfPoc) * totalDays)
        } else {
          const window = forecastModel === 'trailing_7' ? 7 : 14
          const fromDay = Math.max(dayOfPoc - window + 1, 1)
          let trailingSum = 0
          for (let d = fromDay; d <= dayOfPoc; d++) trailingSum += dailyActuals[d] ?? 0
          const trailingAvg = trailingSum / Math.max(dayOfPoc - fromDay + 1, 1)
          forecastDay90 = Math.round(currentActual + trailingAvg * remainingDays)
        }
      }

      return {
        hasPocWindow: true,
        hasMetricTarget,
        hasGoals: hasMetricTarget,
        emptyReason: null,
        clientName,
        pocStartDate: start.toISOString().slice(0, 10),
        pocEndDate: end.toISOString().slice(0, 10),
        dayOfPoc,
        totalDays,
        m1Target: m1,
        m2Target: m2,
        m3Target: m3,
        currentActual,
        forecastDay90,
        forecastModel,
        series,
        diagnostic,
        loading: false,
      }
    },
  })

  return {
    ...(query.data ?? EMPTY),
    loading: query.isLoading,
  }
}
